import React from "react";
import { Trash2, RotateCcw } from "lucide-react";
import { ClaimProductColumnHeader } from "src/enums";
import { ClaimProductInputField, TableColumn } from "src/types";
import { TextInputBox } from "src/components/ui/inputs";
import { Table } from "src/components";

interface ClaimProductInputTableProps {
  products: ClaimProductInputField[];
  setProducts: React.Dispatch<React.SetStateAction<ClaimProductInputField[]>>;
  minProducts?: number;
}

const ClaimProductInputTable: React.FC<ClaimProductInputTableProps> = ({ products, setProducts, minProducts = 1 }) => {
  const handleProductInputChange = <T,>(id: string, fieldName: keyof ClaimProductInputField, value: T) => {
    setProducts(products.map((product) => (product.fieldId === id ? { ...product, [fieldName]: value } : product)));
  };

  const handleDeleteProduct = (row: ClaimProductInputField) => {
    const nonDeletedProducts = products.filter((p) => !p.deleted);
    if (nonDeletedProducts.length > minProducts) {
      setProducts(
        products.map((product) =>
          product.fieldId === row.fieldId
            ? {
                ...product,
                deleted: true,
                originalClaimQuantity: product.claimQuantity || "", // Store original quantity
                claimQuantity: "0"
              }
            : product
        )
      );
    }
  };

  const handleRestoreProduct = (row: ClaimProductInputField) => {
    setProducts(
      products.map((product) =>
        product.fieldId === row.fieldId
          ? {
              ...product,
              deleted: false,
              claimQuantity: product.originalClaimQuantity || product.claimQuantity || "" // Restore original quantity
            }
          : product
      )
    );
  };

  const isRowDeleted = (row: ClaimProductInputField) => row.deleted === true;
  const nonDeletedCount = products.filter((p) => !p.deleted).length;

  const ClaimProductInputTableColumns: TableColumn<ClaimProductColumnHeader, ClaimProductInputField>[] = [
    {
      header: ClaimProductColumnHeader.PRODUCT_ID,
      column: (row: ClaimProductInputField) => (
        <span
          className={`text-sm font-mono ${isRowDeleted(row) ? "line-through decoration-error-500 decoration-2 text-secondary-500" : "text-secondary-900"}`}
        >
          {row.productId || "-"}
        </span>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: ClaimProductColumnHeader.PRODUCT_NAME,
      column: (row: ClaimProductInputField) => (
        <span
          className={`text-sm ${isRowDeleted(row) ? "line-through decoration-error-500 decoration-2 text-secondary-500" : "text-secondary-900"}`}
        >
          {row.productName || "-"}
        </span>
      ),
      styles: { minWidth: "200px" }
    },
    {
      header: ClaimProductColumnHeader.PRODUCT_SKU,
      column: (row: ClaimProductInputField) => (
        <span
          className={`text-sm font-mono ${isRowDeleted(row) ? "line-through decoration-error-500 decoration-2 text-secondary-500" : "text-secondary-900"}`}
        >
          {row.sku || "-"}
        </span>
      ),
      styles: { minWidth: "150px" }
    },
    {
      header: ClaimProductColumnHeader.BARCODE,
      column: (row: ClaimProductInputField) => (
        <span
          className={`text-sm font-mono ${isRowDeleted(row) ? "line-through decoration-error-500 decoration-2 text-secondary-500" : "text-secondary-900"}`}
        >
          {row.barcode || "-"}
        </span>
      ),
      styles: { minWidth: "150px" }
    },
    {
      header: ClaimProductColumnHeader.QUANTITY,
      column: (row: ClaimProductInputField) => (
        <span
          className={`text-sm ${isRowDeleted(row) ? "line-through decoration-error-500 decoration-2 text-secondary-500" : "text-secondary-900"}`}
        >
          {row.quantity || "-"}
        </span>
      ),
      styles: { minWidth: "100px", textAlign: "center" }
    },
    {
      header: ClaimProductColumnHeader.UNIT_PRICE,
      column: (row: ClaimProductInputField) => (
        <span
          className={`text-sm ${isRowDeleted(row) ? "line-through decoration-error-500 decoration-2 text-secondary-500" : "text-secondary-900"}`}
        >
          {row.unitPrice ? `£${row.unitPrice.toFixed(2)}` : "-"}
        </span>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: ClaimProductColumnHeader.QUANTITY_DAMAGED,
      column: (row: ClaimProductInputField) => (
        <TextInputBox
          id={`claim-product-quantity-${row.fieldId}`}
          value={row.claimQuantity ?? ""}
          onChange={(value) => handleProductInputChange(row.fieldId, "claimQuantity", value)}
          maxLength={10}
          placeholder="Qty"
          size="sm"
          type="text"
          disabled={isRowDeleted(row)}
        />
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: ClaimProductColumnHeader.PLACE_HOLDER,
      column: (row: ClaimProductInputField) => {
        if (isRowDeleted(row)) {
          return (
            <button
              onClick={() => handleRestoreProduct(row)}
              className="group flex items-center justify-center p-2 rounded-md hover:bg-success-50 transition-colors mx-auto"
              title="Restore product"
              type="button"
            >
              <RotateCcw className="w-4 h-4 text-success-600 group-hover:text-success-700 transition-colors" />
            </button>
          );
        }
        return (
          <button
            onClick={() => handleDeleteProduct(row)}
            disabled={nonDeletedCount <= minProducts}
            className="group flex items-center justify-center p-2 rounded-md hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent mx-auto"
            title="Delete product"
            type="button"
          >
            <Trash2 className="w-4 h-4 text-error-600 group-hover:text-error-700 transition-colors" />
          </button>
        );
      },
      styles: { minWidth: "60px", textAlign: "center" }
    }
  ];

  return <Table columns={ClaimProductInputTableColumns} data={products} />;
};

export { ClaimProductInputTable };
